const SPECIAL_COLUMN_IDS = new Set([
  '__checkbox-column',
  '__row-index-column',
  '__row-expand-column',
  '__row-reorder-column'
]);

const getColumnId = column => column?.id || column?.name;

const getGridRoot = computedProps =>
  computedProps?.domRef?.current || computedProps?.getDOMNode?.();

const getColumnElements = (root, className, columnId) => {
  if (!root || !columnId) return [];

  return Array.from(root.querySelectorAll(className)).filter(
    element =>
      element.getAttribute('data-props-id') === columnId ||
      element.getAttribute('data-state-props-id') === columnId
  );
};

const getHeaderElement = (computedProps, columnId) => {
  const header = computedProps?.getHeader?.();
  const headerCells = header?.getCells?.() || [];
  const headerCell = headerCells
    .map(cell => ({
      id: cell?.props?.id,
      node: cell?.getDOMNode?.()
    }))
    .find(cell => cell.id === columnId)?.node;

  if (!headerCell) return null;

  if (
    headerCell.classList?.contains(
      'InovuaReactDataGrid__column-header__resize-wrapper'
    )
  ) {
    return Array.from(headerCell.children).find(child =>
      child.classList?.contains('InovuaReactDataGrid__column-header')
    );
  }

  return headerCell;
};

const getContentWidth = element => {
  if (!element) return 0;

  const content =
    element.querySelector?.('.InovuaReactDataGrid__cell__content') ||
    element.querySelector?.('.InovuaReactDataGrid__column-header__content') ||
    element;
  const rect = content.getBoundingClientRect?.();

  return Math.ceil(
    Math.max(content.scrollWidth || 0, content.offsetWidth || 0, rect?.width || 0)
  );
};

const clampColumnWidth = (column, width) => {
  let nextWidth = Math.ceil(width);

  if (column?.computedMinWidth && nextWidth < column.computedMinWidth) {
    nextWidth = column.computedMinWidth;
  }
  if (column?.minWidth && nextWidth < column.minWidth) {
    nextWidth = column.minWidth;
  }
  if (column?.computedMaxWidth && nextWidth > column.computedMaxWidth) {
    nextWidth = column.computedMaxWidth;
  }
  if (column?.maxWidth && nextWidth > column.maxWidth) {
    nextWidth = column.maxWidth;
  }

  return nextWidth;
};

export const measureColumnAutoWidth = (computedProps, columnId) => {
  const root = getGridRoot(computedProps);
  const column = computedProps?.getColumnBy?.(columnId);
  if (!root || !column || column.resizable === false) return null;

  const headerWidth = getContentWidth(getHeaderElement(computedProps, columnId));
  const cellWidths = getColumnElements(
    root,
    '.InovuaReactDataGrid__cell',
    columnId
  ).map(getContentWidth);
  const measuredWidth = Math.max(headerWidth, ...cellWidths);

  if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) return null;

  return {
    column,
    width: clampColumnWidth(column, measuredWidth + 36)
  };
};

export const measureAllColumnsAutoWidths = computedProps => {
  const visibleColumns = computedProps?.visibleColumns || [];

  return visibleColumns
    .filter(column => {
      const columnId = getColumnId(column);
      return columnId && !SPECIAL_COLUMN_IDS.has(columnId);
    })
    .map(column => measureColumnAutoWidth(computedProps, getColumnId(column)))
    .filter(Boolean);
};

export const measureColumnsToFitWidths = computedProps => {
  const root = getGridRoot(computedProps);
  const visibleColumns = computedProps?.visibleColumns || [];
  if (!root || !visibleColumns.length) return [];

  const availableWidth =
    root.querySelector('.InovuaReactDataGrid__header')?.getBoundingClientRect()
      ?.width || root.getBoundingClientRect?.().width || 0;

  if (!availableWidth) return [];

  const adjustableColumns = [];
  const fixedWidth = visibleColumns.reduce((sum, column) => {
    const columnId = getColumnId(column);
    const width = column.computedWidth || column.width || column.defaultWidth || 0;

    if (
      !columnId ||
      SPECIAL_COLUMN_IDS.has(columnId) ||
      column.resizable === false
    ) {
      return sum + width;
    }

    adjustableColumns.push(column);
    return sum;
  }, 0);

  const adjustableCurrentWidth = adjustableColumns.reduce(
    (sum, column) =>
      sum + (column.computedWidth || column.width || column.defaultWidth || 0),
    0
  );
  const availableForAdjustable = Math.max(0, availableWidth - fixedWidth);

  if (!adjustableColumns.length || !adjustableCurrentWidth || !availableForAdjustable) {
    return [];
  }

  return adjustableColumns.map(column => {
    const currentWidth =
      column.computedWidth || column.width || column.defaultWidth || 0;
    const nextWidth =
      (currentWidth / adjustableCurrentWidth) * availableForAdjustable;

    return {
      column,
      width: clampColumnWidth(column, nextWidth)
    };
  });
};

export const getAutosizeColumnUpdates = (
  computedProps,
  columnId,
  autosizeMode
) => {
  if (autosizeMode === 'fit') {
    return measureColumnsToFitWidths(computedProps);
  }
  if (autosizeMode === 'all') {
    return measureAllColumnsAutoWidths(computedProps);
  }

  const update = measureColumnAutoWidth(computedProps, columnId);
  return update ? [update] : [];
};
